import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, FileText, Paperclip, RefreshCw } from 'lucide-react';

import { Badge, Button, Drawer } from '@/design-system/primitives';
import {
  usePatientBrief,
  usePatientChatAttachments,
} from '@/lib/api/clinicianHooks';
import type { BriefDocument, PatientChatAttachment } from '@/types/clinician';
import { DocumentPreview } from './PatientDocumentPreview';

type FileRow = {
  key: string;
  source: 'document' | 'chat';
  detail: string;
  document: BriefDocument;
};
type Filter = 'all' | 'document' | 'chat';

const PURPOSES: Record<BriefDocument['purpose'], string> = {
  id: 'Photo ID',
  lab: 'Lab results',
  prescription: 'Prescription',
  transfer_prescription: 'Prior prescription',
  other: 'Document',
};

// File URLs are supplied by the authorized record APIs. Never render an active
// scheme (e.g. javascript:) from an imported document as a preview/download URL.
function safeFileUrl(url: string | null) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return ['https:', 'http:'].includes(parsed.protocol) ? url : null;
  } catch {
    return null;
  }
}

export function patientFileRows(
  documents: BriefDocument[],
  attachments: PatientChatAttachment[],
): FileRow[] {
  return [
    ...documents.map(
      (document): FileRow => ({
        key: `document:${document.id}`,
        source: 'document',
        detail: PURPOSES[document.purpose] ?? 'Document',
        document: {
          ...document,
          download_url: safeFileUrl(document.download_url),
        },
      }),
    ),
    ...attachments.map(
      (item): FileRow => ({
        key: `chat:${item.id}`,
        source: 'chat',
        detail:
          item.direction === 'patient'
            ? 'Chat · Patient upload'
            : 'Chat · Sent by care team',
        document: {
          id: item.id,
          filename:
            item.filename ||
            (item.kind === 'image' ? 'Chat image' : 'Chat document'),
          mime:
            item.mime_type ||
            (item.kind === 'image' ? 'image/*' : 'application/octet-stream'),
          size_bytes: item.size_bytes ?? 0,
          purpose: 'other',
          uploaded_at: item.message_at,
          download_url: safeFileUrl(item.url),
        },
      }),
    ),
  ].sort(
    (a, b) =>
      (Date.parse(b.document.uploaded_at) || 0) -
      (Date.parse(a.document.uploaded_at) || 0),
  );
}

export function PatientFilesCard({
  conversationId,
  synthetic = false,
}: {
  conversationId: string;
  synthetic?: boolean;
}) {
  const brief = usePatientBrief(conversationId);
  const syntheticRecord = synthetic || Boolean(brief.data?.synthetic);
  const attachments = usePatientChatAttachments(
    conversationId,
    !syntheticRecord,
  );
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<{
    key: string;
    conversationId: string;
  } | null>(null);
  const [opening, setOpening] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const previewRequest = useRef(0);
  const documents = brief.data?.documents ?? [];
  const chatFiles = syntheticRecord ? [] : (attachments.data ?? []);
  const rows = patientFileRows(documents, chatFiles);
  const visible = rows.filter(
    (row) => filter === 'all' || row.source === filter,
  );
  const selectedRow =
    selected?.conversationId === conversationId
      ? rows.find((row) => row.key === selected.key)
      : undefined;
  const loading =
    brief.isLoading || (!syntheticRecord && attachments.isLoading);
  const failed = brief.isError || (!syntheticRecord && attachments.isError);
  const refreshing =
    brief.isFetching || (!syntheticRecord && attachments.isFetching);

  const refresh = () => {
    void brief.refetch();
    if (!syntheticRecord) void attachments.refetch();
  };
  const preview = async (row: FileRow) => {
    const request = ++previewRequest.current;
    setSelected({ key: row.key, conversationId });
    setPreviewError(false);
    setOpening(false);
    const query = row.source === 'document' ? brief : attachments;
    // Storage links expire after 15 minutes. Refresh old records before opening
    // a file in a long-running conversation, including failed link creation.
    if (
      !row.document.download_url ||
      Date.now() - query.dataUpdatedAt > 5 * 60_000
    ) {
      setOpening(true);
      try {
        const result = await query.refetch();
        if (request === previewRequest.current) setPreviewError(result.isError);
      } catch {
        if (request === previewRequest.current) setPreviewError(true);
      } finally {
        if (request === previewRequest.current) setOpening(false);
      }
    }
  };

  return (
    <section
      aria-label="Patient files"
      className="p-12 min-w-0 flex flex-col gap-9"
    >
      <div className="flex items-center justify-between gap-8">
        <h3 className="flex items-center gap-6 type-label-sm font-medium">
          <Paperclip size={13} aria-hidden /> Patient files
          {rows.length > 0 && (
            <Badge tone="neutral" size="sm">
              {rows.length}
            </Badge>
          )}
        </h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={refresh}
          disabled={refreshing}
          aria-label="Refresh patient files"
        >
          <RefreshCw
            size={13}
            className={refreshing ? 'animate-spin' : ''}
            aria-hidden
          />
        </Button>
      </div>
      <p className="text-11 text-ink-muted">
        Portal uploads, chart documents, and chat attachments—all available
        without leaving the conversation.
      </p>
      <div
        className="flex flex-wrap gap-5"
        role="group"
        aria-label="Filter patient files"
      >
        {(
          [
            ['all', 'All', rows.length],
            ['document', 'Documents', documents.length],
            ['chat', 'Chat', chatFiles.length],
          ] as const
        ).map(([value, label, count]) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
            className={`type-label-xs border rounded px-7 py-5 focus-visible:outline-none focus-visible:shadow-focus ${filter === value ? 'bg-ink text-page border-ink' : 'border-line text-ink-muted hover:bg-sunken'}`}
          >
            {label} ({count})
          </button>
        ))}
      </div>
      {loading && (
        <p role="status" className="type-body-sm text-ink-muted">
          Loading patient files…
        </p>
      )}
      {brief.isError && (
        <p role="alert" className="type-body-sm text-warning">
          Portal and chart documents are temporarily unavailable. Refresh to
          retry.
        </p>
      )}
      {!syntheticRecord && attachments.isError && (
        <p role="alert" className="type-body-sm text-warning">
          Chat attachments are temporarily unavailable. Refresh to retry.
        </p>
      )}
      {!loading && !failed && visible.length === 0 && (
        <p className="type-body-sm text-ink-muted">
          {filter === 'document'
            ? 'No portal or chart documents on file.'
            : filter === 'chat'
              ? 'No chat attachments on file.'
              : 'No patient files on record.'}
        </p>
      )}
      {visible.length > 0 && (
        <ul
          className="flex max-h-[22rem] flex-col gap-7 overflow-y-auto pr-2"
          aria-label="Patient file list"
        >
          {visible.map((row) => (
            <li key={row.key}>
              <button
                type="button"
                onClick={() => void preview(row)}
                aria-label={`Preview ${row.document.filename}`}
                className="w-full text-left flex items-center gap-8 rounded border border-line-subtle p-7 hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus"
              >
                {row.document.mime.startsWith('image/') &&
                row.document.download_url ? (
                  <img
                    src={row.document.download_url}
                    alt=""
                    loading="lazy"
                    className="h-38 w-38 shrink-0 rounded border border-line-subtle object-cover"
                  />
                ) : (
                  <span className="flex h-38 w-38 shrink-0 items-center justify-center rounded border border-line-subtle bg-sunken">
                    <FileText size={15} aria-hidden />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block break-words type-body-sm font-medium">
                    {row.document.filename}
                  </span>
                  <span className="block text-11 text-ink-muted">
                    {row.detail} ·{' '}
                    {Number.isFinite(Date.parse(row.document.uploaded_at))
                      ? new Date(row.document.uploaded_at).toLocaleDateString()
                      : 'Date unavailable'}
                  </span>
                  {!row.document.download_url && (
                    <span className="block text-11 text-warning">
                      Link unavailable · click to retry
                    </span>
                  )}
                </span>
                <Eye
                  size={14}
                  className="shrink-0 text-ink-muted"
                  aria-hidden
                />
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected?.conversationId === conversationId &&
        createPortal(
          <Drawer
            open
            onClose={() => {
              previewRequest.current++;
              setSelected(null);
            }}
            title="Patient file preview"
            size="wide"
          >
            {opening ? (
              <p role="status" className="p-15 type-body-sm">
                Refreshing secure preview…
              </p>
            ) : previewError ? (
              <p role="alert" className="p-15 type-body-sm text-warning">
                Could not refresh this file. Close the preview and try again.
              </p>
            ) : selectedRow ? (
              <DocumentPreview document={selectedRow.document} />
            ) : (
              <p className="p-15 type-body-sm text-ink-muted">
                This file is no longer available. Refresh the patient files.
              </p>
            )}
          </Drawer>,
          document.body,
        )}
    </section>
  );
}
